import { useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@altimateai/lego";
import {
  UserGroupEntities,
  UserGroupEntitiesLabelsAndValues,
  DatabricksUserGroupEntitiesLabelsAndValues,
  UserGroupRuleTypes,
} from "./types";
import { useAppState } from "@/modules/app/useAppContext";

interface AccessRuleFormData {
  rules: Array<{
    entity_type: UserGroupEntities;
    match_type: UserGroupRuleTypes;
    entity: string;
    key?: string;
  }>;
}

interface AccessRuleFormFieldsProps {
  form: UseFormReturn<AccessRuleFormData>;
  index: number;
}

const AccessRuleFormFields = ({ form, index }: AccessRuleFormFieldsProps) => {
  const { isDatabricksTenant } = useAppState();
  const entityType = form.watch(`rules.${index}.entity_type`);

  // Databricks tenants scope teams by account / workspace; Snowflake tenants
  // use the warehouse/database/schema/user/role/query_tag entity types.
  const entityTypeOptions = useMemo(
    () =>
      isDatabricksTenant
        ? DatabricksUserGroupEntitiesLabelsAndValues
        : UserGroupEntitiesLabelsAndValues,
    [isDatabricksTenant]
  );

  const matchTypeOptions = useMemo(() => {
    return entityType === UserGroupEntities.QUERY_TAG
      ? Object.values(UserGroupRuleTypes).filter(type => type === UserGroupRuleTypes.EXACT)
      : Object.values(UserGroupRuleTypes);
  }, [entityType]);

  return (
    <div className="al-grid al-grid-cols-2 al-gap-4 al-mt-2">
      <FormField
        control={form.control}
        name={`rules.${index}.entity_type`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Select entity type</FormLabel>
            <Select
              onValueChange={(value: string) => {
                field.onChange(value);
                form.setValue(`rules.${index}.entity`, "");
                form.setValue(`rules.${index}.key`, "");
                if (
                  value === UserGroupEntities.DATASTORE ||
                  value === UserGroupEntities.DBT_INTEGRATION
                ) {
                  form.setValue(`rules.${index}.match_type`, UserGroupRuleTypes.EXACT);
                }
              }}
              defaultValue={field.value}
            >
              <FormControl>
                <SelectTrigger className="al-w-full">
                  <SelectValue placeholder="Select entity type" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {entityTypeOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name={`rules.${index}.match_type`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Select match type</FormLabel>
            <Select
              onValueChange={(value: string) => {
                field.onChange(value);
                form.setValue(`rules.${index}.entity`, "");
                form.setValue(`rules.${index}.key`, "");
              }}
              defaultValue={field.value}
              disabled={
                entityType === UserGroupEntities.DATASTORE ||
                entityType === UserGroupEntities.DBT_INTEGRATION
              }
            >
              <FormControl>
                <SelectTrigger className="al-w-full">
                  <SelectValue placeholder="Select match type" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {matchTypeOptions.map(type => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};

export default AccessRuleFormFields;
